import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, MoreHorizontal, Calendar, Settings, X } from 'lucide-react';
import clsx from 'clsx';
import { useModuleLabels } from '../shared/hooks/useModuleLabels.js';
import { ModuleIcon } from '../shared/utils/iconMap.js';
import { useAuth } from '../auth/AuthContext.js';
import { toonInMenu } from './rolProfiel.js';

interface BottomItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  match?: string;
}

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const labels = useModuleLabels();
  const [meerOpen, setMeerOpen] = useState(false);

  const { user } = useAuth();
  const items: BottomItem[] = useMemo(() => [
    { icon: <LayoutDashboard size={20} />,                            label: 'Dashboard',              path: '/dashboard',     match: '/dashboard' },
    { icon: <ModuleIcon name={labels.communicatie.icon} size={20} />, label: 'Communicatie',            path: '/communicatie',  match: '/communicatie' },
    { icon: <ModuleIcon name={labels.crm.icon} size={20} />,         label: labels.crm.label,          path: '/crm',           match: '/crm' },
    { icon: <ModuleIcon name={labels.buitendienst.icon} size={20} />, label: labels.buitendienst.label, path: '/buitendienst', match: '/buitendienst' },
    { icon: <ModuleIcon name={labels.logistiek.icon} size={20} />,    label: labels.logistiek.label,   path: '/logistiek',     match: '/logistiek' },
  ].filter((i) => toonInMenu(user?.role, i.path)), [labels, user?.role]);

  const meerItems: BottomItem[] = useMemo(() => [
    { icon: <ModuleIcon name={labels.planning.icon} size={20} />, label: labels.planning.label, path: '/planning', match: '/planning' },
    { icon: <Settings size={20} />,                                label: 'Instellingen',        path: '/settings', match: '/settings' },
  ].filter((i) => toonInMenu(user?.role, i.path)), [labels, user?.role]);

  const isActive = (item: BottomItem) => {
    if (!item.match) return location.pathname === item.path;
    return location.pathname.startsWith(item.match);
  };

  const isMeerActive = meerItems.some(i => isActive(i));

  return (
    <>
      {/* Bottom bar */}
      <nav aria-label="Hoofdnavigatie" className="md:hidden fixed bottom-0 left-0 right-0 h-[60px] bg-white border-t border-gray-200 flex items-center justify-around px-2 z-40">
        {items.map(item => {
          const active = isActive(item);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              aria-current={active ? 'page' : undefined}
              className={clsx('relative flex flex-col items-center justify-center gap-0.5 py-1 px-2 min-w-[44px] min-h-[44px] rounded-lg', active && 'bg-brand-light')}
            >
              <span className={active ? 'text-gold' : 'text-gray-500'}>{item.icon}</span>
              <span className={clsx('text-[11px]', active ? 'text-gold font-semibold' : 'text-gray-500 font-medium')}>{item.label}</span>
            </button>
          );
        })}
        {/* Meer button */}
        <button
          onClick={() => setMeerOpen(true)}
          className={clsx('flex flex-col items-center justify-center gap-0.5 py-1 px-2 min-w-[44px] min-h-[44px] rounded-lg', isMeerActive && 'bg-brand-light')}
        >
          <span className={isMeerActive ? 'text-gold' : 'text-gray-500'}><MoreHorizontal size={20} /></span>
          <span className={clsx('text-[11px]', isMeerActive ? 'text-gold font-semibold' : 'text-gray-500 font-medium')}>Meer</span>
        </button>
      </nav>

      {/* Meer sheet */}
      {meerOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setMeerOpen(false)} />

          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Meer</h3>
              <button onClick={() => setMeerOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="py-2 pb-8">
              {meerItems.map(item => {
                const active = isActive(item);
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMeerOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <span className={active ? 'text-gold' : 'text-gray-400'}>{item.icon}</span>
                    <span className={clsx('text-sm font-medium', active ? 'text-gold' : 'text-gray-700')}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
